import { TFunction } from 'i18next';
import { Alert } from 'lib/alert/models';
import { translateBlock } from 'i18n';
import { capitalize } from 'helpers/helpers';

/**
 * This helpers allows to get the metrics we want to be selected to be a metric
 * for the alert. We keep only the metrics of the chart that are either a 'sum'
 * or an 'average'. If the chart has a metric on a table, we
 * want to allow all the sum and average metrics of this table.
 * @param {SentenceMetricT[]} chartMetrics - The metrics of the chart.
 * @param {AvailableMetricT[]} availableMetrics - All the available metrics.
 * @return AvailableMetricT[]
 */
export const getAvailableAlertMetrics = (
    chartMetrics: SentenceMetricT[],
    availableMetrics: AvailableMetricT[],
): AvailableMetricT[] => {
    const availableAlertMetrics: AvailableMetricT[] = [];

    chartMetrics.forEach((chartMetric) => {
        // If the metric of the chart is a sum, we add it to the possible
        // alert metric
        if (
            chartMetric.aggregation === 'sum' ||
            chartMetric.aggregation === 'average'
        ) {
            const availableMetric = availableMetrics.find(
                (m) => m.field === chartMetric.field,
            );

            if (availableMetric) {
                availableAlertMetrics.push(availableMetric);
            }
        }

        // If the metric of the chart is from table, we add the number metrics for the
        // table.
        if (
            chartMetric.aggregation === 'list' ||
            chartMetric.aggregation === 'number'
        ) {
            const availableMetricsForTable = availableMetrics.filter(
                (m) =>
                    m.table === chartMetric.field &&
                    (m.aggregations.includes('sum') ||
                        m.aggregations.includes('average')),
            );

            availableMetricsForTable.forEach((m) =>
                availableAlertMetrics.push(m),
            );
        }
    });

    return availableAlertMetrics;
};

/**
 * This function retrieves the symbol associated with the given element.
 * @param {DataTypesT} type - The type of the element (e.g : "money")
 */
export const getSymbolForElementType = (type: DataTypesT): string | null => {
    switch (type) {
        case 'money':
            return '€';
        case 'fraction':
            return '%';
        default:
            return null;
    }
};

/**
 * This function is used to display a label that indicates to the user
 * his progress in the alert creation.
 * @param {number} step - The step number.
 * @param {Alert} alert - The partially filled alert object
 * @param {TFunction} t - Translation function
 */
export const getCompletedStepLabel = (
    step: number,
    alert: Partial<Alert>,
    t: TFunction,
): string => {
    let thresholdValue = alert.condition?.threshold;
    if (
        alert.metric?.type !== undefined &&
        alert.metric.type === 'fraction' &&
        thresholdValue !== undefined
    ) {
        thresholdValue *= 100.0;
    }

    switch (step) {
        case 0:
            return t(`alert.types.${alert.type}`);
        case 1:
            return t('alert.create.steps.1.optional', {
                metric: capitalize(translateBlock(alert.metric?.field)),
                operation: t(`alert.create.${alert.condition?.operation}`),
                threshold: thresholdValue,
                type: alert.metric?.type
                    ? getSymbolForElementType(alert.metric?.type)
                    : '',
            });
        case 2:
            return alert.dimension
                ? t('alert.create.steps.2.optional', {
                      dimension: capitalize(
                          translateBlock(alert.dimension.field),
                      ),
                  })
                : t('alert.create.steps.2.description');
        case 3:
            return t('alert.create.steps.3.description');
        case 4:
            return t('alert.create.steps.4.description');
        case 5:
            return t('alert.create.steps.5.description');
        default:
            return '';
    }
};
